/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */
/* eslint-disable @next/next/no-img-element -- deliberate: lightweight
   crawlable tags already served via our image proxy; next/image would add
   srcset weight to HTML that unmounts after hydration */
import { imageProxy } from 'helpers/imageProxy';

// Same filename-junk filter the rest of the post renderer uses
const FILENAME_JUNK = /(DSC_|\.gif|\.jpg|\.jpeg|\.png)/i;
const MAX_STRIP_IMAGES = 4;

const toInt = v => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

// SSR'd placeholder for post-body image galleries: real, crawlable <img>
// tags where ssr:false used to leave a hole. Reserves the same height the
// interactive carousel will occupy (first image's aspect ratio, letterbox
// caps 320/512/640px) so the client-side swap is height-neutral. Must stay
// a deterministic pure function of props — it renders in the SSG HTML and
// must hydrate exactly. Gallery JSON is user content: keep the guards.
export default function GalleryStrip({ images, toggleLightbox }) {
  if (!images || !Array.isArray(images)) return <></>;
  const valid = images.filter(img => img && img.url);
  if (valid.length === 0) return <></>;

  const shown = valid.slice(0, MAX_STRIP_IMAGES);
  const hiddenCount = valid.length - shown.length;
  const [first] = shown;
  const w0 = toInt(first.width);
  const h0 = toInt(first.height);
  const sizeStyle = w0 && h0 ? { aspectRatio: `${w0} / ${h0}` } : undefined;
  const sizeClasses = sizeStyle
    ? ' tw-max-h-80 sm:tw-max-h-128 xl:tw-max-h-160'
    : ' tw-h-80 sm:tw-h-128';
  const caption =
    first.caption && !FILENAME_JUNK.test(first.caption)
      ? first.caption
      : undefined;

  const altFor = img =>
    img.caption && !FILENAME_JUNK.test(img.caption) ? img.caption : '';
  // alwaysConvert: gallery urls are img.travelfeed.io, which imageProxy
  // otherwise returns unresized
  const srcFor = img =>
    imageProxy(img.url, 640, undefined, 'fit', undefined, true);
  const clickFor = img =>
    toggleLightbox ? () => toggleLightbox(img.url) : undefined;
  const cursor = toggleLightbox ? ' tw-cursor-zoom-in' : '';

  if (shown.length === 1)
    return (
      <figure className="fullwidth tf-gallery-block">
        <div
          className={
            'tw-flex tw-w-full tw-justify-center tw-overflow-hidden' +
            sizeClasses
          }
          style={sizeStyle}
        >
          <img
            src={srcFor(first)}
            alt={altFor(first)}
            width={w0}
            height={h0}
            loading="lazy"
            decoding="async"
            className={
              'tf-tile tw-h-full tw-w-auto tw-max-w-full tw-object-contain' +
              cursor
            }
            onClick={clickFor(first)}
          />
        </div>
        {caption && <figcaption>{caption}</figcaption>}
      </figure>
    );

  const isTriple = shown.length === 3;
  return (
    <figure className="fullwidth tf-gallery-block">
      <div
        className={
          // Explicit row count is load-bearing: the cells contain only
          // absolutely-positioned imgs, so implicit auto rows would
          // collapse to 0 height inside the aspect-ratio box. No
          // overflow-hidden here — it would clip the cells' shadows.
          'tf-gallery-sheet tw-grid tw-w-full tw-grid-cols-2 tw-gap-1.5' +
          (shown.length === 2 ? ' tw-grid-rows-1' : ' tw-grid-rows-2') +
          sizeClasses
        }
        style={sizeStyle}
      >
        {shown.map((img, i) => (
          <div
            key={img.url + i}
            className={
              'tf-tile tw-relative tw-overflow-hidden' +
              (isTriple && i === 0 ? ' tw-row-span-2' : '')
            }
          >
            <img
              src={srcFor(img)}
              alt={altFor(img)}
              width={toInt(img.width)}
              height={toInt(img.height)}
              loading="lazy"
              decoding="async"
              className={
                'tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover' +
                cursor
              }
              onClick={clickFor(img)}
            />
            {hiddenCount > 0 && i === shown.length - 1 && (
              <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-[rgba(0,0,0,0.45)] tw-text-lg tw-font-semibold tw-text-white">
                +{hiddenCount}
              </div>
            )}
          </div>
        ))}
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}
